import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';
interface Equipe {
  id: string;
  nom: string;
  membres: string[];
  active: boolean;
  version: number;
}
export function PageEquipes() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [creation, setCreation] = useState(false);
  const [edition, setEdition] = useState<Equipe | null>(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const permis = peut(contexte, 'intervention', 'modifier');
  const equipes = useQuery({
    queryKey: ['equipes-reseau'],
    queryFn: () => requete<{ elements: Equipe[] }>('/equipes-reseau'),
    enabled: permis,
  });
  const agents = useQuery({
    queryKey: ['interventions-reseau', 'agents'],
    queryFn: () =>
      requete<{ elements: { id: string; nom: string; prenoms: string | null }[] }>(
        '/interventions-reseau/agents',
      ),
    enabled: permis,
  });
  if (!permis) return <p role="alert">Gestion des équipes non autorisée.</p>;
  return (
    <section>
      <EnTetePage titre="Équipes d’intervention" />
      <p>
        Les membres sont enregistrés sur chaque mission au moment de l’affectation. Modifier une
        équipe ne réaffecte pas ses missions existantes.
      </p>
      <button
        onClick={() => {
          setEdition(null);
          setCreation(true);
        }}
      >
        Créer une équipe
      </button>
      {creation && (
        <form
          key={edition?.id ?? 'nouvelle'}
          style={{ display: 'grid', gap: 12, maxWidth: 700 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            setErreur('');
            setEnCours(true);
            try {
              await requete(edition ? `/equipes-reseau/${edition.id}` : '/equipes-reseau', {
                methode: edition ? 'PUT' : 'POST',
                corps: {
                  nom: d.get('nom'),
                  membres: d.getAll('membre'),
                  active: d.get('active') === 'on',
                  ...(edition ? { version: edition.version, motif: d.get('motif') } : {}),
                },
              });
              setCreation(false);
              await client.invalidateQueries({ queryKey: ['equipes-reseau'] });
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setEnCours(false);
            }
          }}
        >
          <label>
            Nom de l’équipe{' '}
            <input name="nom" required minLength={3} maxLength={120} defaultValue={edition?.nom} />
          </label>
          <fieldset>
            <legend>Membres habilités (1 à 30)</legend>
            {agents.data?.elements.map((a) => (
              <label key={a.id} style={{ display: 'block' }}>
                <input
                  type="checkbox"
                  name="membre"
                  value={a.id}
                  defaultChecked={edition?.membres.includes(a.id)}
                />
                {[a.prenoms, a.nom].filter(Boolean).join(' ')}
              </label>
            ))}
          </fieldset>
          <label>
            <input type="checkbox" name="active" defaultChecked={edition?.active ?? true} />
            Équipe active
          </label>
          {edition && (
            <label>
              Motif <input name="motif" required minLength={3} maxLength={500} />
            </label>
          )}
          <button disabled={enCours || agents.isPending}>Enregistrer l’équipe</button>
          <button type="button" onClick={() => setCreation(false)}>
            Annuler
          </button>
        </form>
      )}
      {(erreur || equipes.error || agents.error) && (
        <p role="alert">{erreur || messageErreur(equipes.error || agents.error)}</p>
      )}
      {equipes.isPending && <p>Chargement…</p>}
      {equipes.data?.elements.map((e) => (
        <article key={e.id}>
          <h2>{e.nom}</h2>
          <p>
            {e.active ? 'Active' : 'Inactive'} · {e.membres.length} membre(s)
          </p>
          <ul>
            {e.membres.map((id) => (
              <li key={id}>
                {agents.data?.elements.find((a) => a.id === id)?.nom ??
                  'Membre devenu indisponible'}
              </li>
            ))}
          </ul>
          <button
            onClick={() => {
              setEdition(e);
              setCreation(true);
            }}
          >
            Modifier l’équipe
          </button>
        </article>
      ))}
    </section>
  );
}
